import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { TOUR_STEPS } from "@/components/tour/tourSteps";
import { useAuth } from "@/context/AuthContext";

const TourContext = createContext(null);

export function TourProvider({ children }) {
  const { markTutorialSeen } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // Il Dashboard component viene smontato quando si cambia pagina. Questa
  // guardia vive nel provider globale e impedisce di riavviare il tour da zero
  // quando l'utente torna sulla Dashboard durante la stessa sessione.
  const autoStartedForUserRef = useRef(new Set());

  const startTour = useCallback(() => {
    setStepIndex(0);
    setIsActive(true);
  }, []);

  const startTourOnce = useCallback((userId) => {
    if (!userId || autoStartedForUserRef.current.has(userId)) return false;
    autoStartedForUserRef.current.add(userId);
    startTour();
    return true;
  }, [startTour]);

  const skipTour = useCallback(() => {
    setIsActive(false);
    // Aggiorna subito AuthContext e cache locale, poi persiste sul backend.
    // In questo modo il ritorno alla Dashboard non può riaprire il tour.
    if (markTutorialSeen) {
      markTutorialSeen();
    } else {
      api.patch("/auth/me", { tutorial_seen: true }).catch(() => {});
    }
  }, [markTutorialSeen]);

  const nextStep = useCallback(() => {
    if (stepIndex >= TOUR_STEPS.length - 1) {
      skipTour();
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [stepIndex, skipTour]);

  const value = useMemo(
    () => ({ isActive, stepIndex, startTour, startTourOnce, nextStep, skipTour }),
    [isActive, stepIndex, startTour, startTourOnce, nextStep, skipTour]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour() {
  return useContext(TourContext);
}
